import { useIgnitionLazy, useFernLazy } from '@squareup/dex-data-access-oas';
import { getDeveloperApi } from '@squareup/dex-data-shared-developer-api';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { useSelectSpecVersionMetadata } from '@squareup/dex-store-access-dex-tech-main-spec-version-hooks';
import { useCodeLanguage } from '@squareup/dex-store-access-shared-main-selectors';
// TODO DPW-801: Move ReferenceCodeBlock to a ui library
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ReferenceCodeBlock } from '@squareup/dex-tech-ui';
import { CodeExampleLanguage } from '@squareup/dex-types-oas';
import { SpecificationVersionInfo } from '@squareup/dex-types-oas-path';
import { CodeBlockEndpointTitle } from '@squareup/dex-ui';
import {
  useApiExplorerAccessToken,
  useApiExplorerEndpoint,
  useApiExplorerInput,
  useApiExplorerEnvironment,
} from '@squareup/dex-ui-shared-api-explorer-providers';
import { useDecision } from '@squareup/dex-ui-shared-feature-detection';
import { isStagingEnvironment } from '@squareup/dex-utils-environment';
import {
  golangClientSetup,
  languageToIgnitionLanguage,
  toFernInput,
} from '@squareup/dex-utils-oas';
import { useTransformInputForExternal } from '@squareup/dex-utils-shared-api-explorer';
import { log } from '@squareup/dex-utils-shared-logging';
import React, { FC, useMemo } from 'react';

const strings = {
  getStarted: 'Select an API and endpoint to get started.',
  error: {
    codegen:
      'An error generating code. We are investigating. Please switch languages, or modify your input in the meantime.',
  },
};

interface Props {}

function generateComment(language: CodeExampleLanguage): string {
  let comment = strings['getStarted'];
  switch (language) {
    case 'curl':
    case 'python':
    case 'ruby':
    case 'php':
      comment = `# ${comment}`;
      break;
    case 'java':
    case 'csharp':
    case 'javascript':
    case 'go':
    default:
      comment = `// ${comment}`;
  }
  return comment;
}

const ApiExplorerRequestContentContainer: FC<Props> = () => {
  const endpoint = useApiExplorerEndpoint();
  const [apiExplorerInput] = useApiExplorerInput();
  const input = useTransformInputForExternal(apiExplorerInput, endpoint);

  const accessToken = useApiExplorerAccessToken();
  const environment = useApiExplorerEnvironment();

  const [selectedCodeLanguage, setCodeLanguage, isLanguageLoading] =
    useCodeLanguage();
  const ignition = useIgnitionLazy();

  const { isVersionSelectionLatest } = useSelectSpecVersionMetadata();

  const codeLanguage = useMemo(() => {
    if (isVersionSelectionLatest) {
      return selectedCodeLanguage;
    }

    return 'curl';
  }, [isVersionSelectionLatest, selectedCodeLanguage]);

  const fernOptions = useMemo(() => {
    return {
      grammar: 'open-api-3',
      version: 'latest',
      isUnpublished: false,
      namespace: 'square',
    } as SpecificationVersionInfo;
  }, []);

  const { fern } = useFernLazy(
    fernOptions,
    endpoint?.name || '',
    codeLanguage,
    getDeveloperApi()
  );

  const {
    decision: { enabled: fernGoExamples },
  } = useDecision('fern_go_examples');
  const {
    decision: { enabled: fernJavaExamples },
  } = useDecision('fern_java_examples');
  const {
    decision: { enabled: fernPythonExamples },
  } = useDecision('fern_python_examples');
  const {
    decision: { enabled: fernRubyExamples },
  } = useDecision('fern_ruby_examples');
  const {
    decision: { enabled: fernPhpExamples },
  } = useDecision('fern_php_examples');
  const {
    decision: { enabled: fernDotNetExamples },
  } = useDecision('fern_dotnet_examples');
  const {
    decision: { enabled: fernNodeExamples },
  } = useDecision('fern_node_examples');

  const code = useMemo(() => {
    if (!ignition) {
      return '';
    }

    const {
      ApiExplorerParameterHandler,
      Language,
      createClientCodeStatement,
      generateCode,
    } = ignition;

    const ignitionLang =
      languageToIgnitionLanguage(codeLanguage) || Language.curl;

    const params = {
      ...ApiExplorerParameterHandler.createParams(),
      ...input,
    };

    const requestEnvironment = (
      isStagingEnvironment() ? 'staging' : environment.toLowerCase()
    ) as Parameters<
      typeof ApiExplorerParameterHandler.convertParamsToCodegenInput
    >[2];

    if (!endpoint) {
      const comment = generateComment(codeLanguage);
      const clientSetupCode =
        codeLanguage === 'go'
          ? golangClientSetup()
          : createClientCodeStatement(
              ignitionLang,
              ApiExplorerParameterHandler.convertParamsToCodegenInput(
                params,
                accessToken,
                requestEnvironment
              ),
              { addImports: true }
            );
      return clientSetupCode
        ? `${comment}\n\n${clientSetupCode.toString()}`
        : comment;
    }

    let shouldUseFern = false;
    switch (codeLanguage) {
      case 'go':
        shouldUseFern = fernGoExamples;
        break;
      case 'java':
        shouldUseFern = fernJavaExamples;
        break;
      case 'python':
        shouldUseFern = fernPythonExamples;
        break;
      case 'ruby':
        shouldUseFern = fernRubyExamples;
        break;
      case 'php':
        shouldUseFern = fernPhpExamples;
        break;
      case 'csharp':
        shouldUseFern = fernDotNetExamples;
        break;
      case 'javascript':
        shouldUseFern = fernNodeExamples;
        break;
      default:
        shouldUseFern = false;
    }

    if (shouldUseFern && fern) {
      const codegenInput =
        ApiExplorerParameterHandler.convertParamsToCodegenInput(
          params,
          accessToken,
          requestEnvironment
        );

      try {
        const res = fern.generateSync(
          toFernInput(codegenInput, endpoint, accessToken, environment)
        );

        return res.snippet;
      } catch (error) {
        log.error('Error generating code with Fern', undefined, error as Error);

        // Return an error to the user
        return `// ${strings.error.codegen}`;
      }
    }

    try {
      return generateCode(
        ignitionLang,
        endpoint,
        ApiExplorerParameterHandler.convertParamsToCodegenInput(
          params,
          accessToken,
          requestEnvironment
        )
      );
    } catch (error) {
      log.error(
        'Error generating code with ignition',
        undefined,
        error as Error
      );

      // Return an error to the user
      return `// ${strings.error.codegen}`;
    }
  }, [
    ignition,
    codeLanguage,
    input,
    environment,
    endpoint,
    fern,
    accessToken,
    fernGoExamples,
    fernJavaExamples,
    fernPythonExamples,
    fernRubyExamples,
    fernPhpExamples,
    fernDotNetExamples,
    fernNodeExamples,
  ]);

  return (
    <ReferenceCodeBlock
      title={<CodeBlockEndpointTitle endpoint={endpoint} textVariant="20" />}
      isRequest={true}
      addCommentTypingAnimation={true}
      currentLanguage={codeLanguage}
      onLanguageChanged={setCodeLanguage}
      code={code}
      isLoading={!ignition}
      isLanguageLoading={isLanguageLoading}
      isCodeLangLimited={!isVersionSelectionLatest}
      fillParentHeight={true}
    />
  );
};

export { ApiExplorerRequestContentContainer };
